/**
 * What the graphs of the dev tools (the decision tree, the ledger graphs) are
 * built from: graphs in the DOT language whose nodes are cards, drawn in the
 * colours of the app's theme.
 *
 * A card is a graphviz node whose label is a table in graphviz's HTML-like
 * markup: that is the only way to get rows, columns and badges into a node.
 * Graphviz lays the graph out, so it has to know the colours as it draws:
 * they are handed to it as literals taken from the theme (see graphColors),
 * and a user with a palette of their own gets their own colours.
 */

/** A tone of a figure or a state: the colour to write in, and its wash. */
export interface GraphTone {
  ink: string;
  wash: string;
}

export interface GraphColors {
  ink: string;
  /** secondary text, and the labels of sections and arrows */
  quiet: string;
  /** the outline of a card */
  border: string;
  /** a line between the rows of a card */
  rule: string;
  /** a card */
  surface: string;
  /** a frame around cards, and the head of a table */
  soft: string;
  arrow: string;
  primary: string;
  incurred: string;
  estimated: string;
  favourable: GraphTone;
  adverse: GraphTone;
  warning: GraphTone;
  neutral: GraphTone;
}

/** The tone of a signed figure where above zero is the good direction. */
export const signedTone = (colors: GraphColors, value: number): GraphTone =>
  value > 0 ? colors.favourable : value < 0 ? colors.adverse : colors.neutral;

/**
 * Graphviz sizes a card by the text in it, measured with the metrics of this
 * font, which it knows; the page then draws the text in the app's own font
 * (see Graph), which graphviz does not know. Measured against Inter in a
 * browser, graphviz's Verdana is 2% to 17% wider, so nothing outgrows its
 * cell; its Helvetica is up to 11% narrower, and amounts ran over the edge
 * of their cards.
 */
const MEASURED_FONT = "Verdana";

/** The opening of a graph: left to right, cards as nodes, quiet arrows. */
export const graphStart = (
  colors: GraphColors,
  graphAttributes = "",
): string[] => [
  `digraph G {`,
  `  graph [rankdir=LR, bgcolor="transparent", nodesep=0.3, ranksep=0.7, pad=0.15, fontname="${MEASURED_FONT}"${graphAttributes}];`,
  `  node [fontname="${MEASURED_FONT}", fontsize=10.5, fontcolor="${colors.ink}", shape=plain];`,
  `  edge [fontname="${MEASURED_FONT}", fontsize=9.5, fontcolor="${colors.quiet}", color="${colors.arrow}", arrowsize=0.6, penwidth=1];`,
];

/**
 * A text of the case (a name, a title) as text of a label. In the markup of
 * a label a `<` opens a tag, and one that is not a tag makes the whole graph
 * fail to lay out; a backslash starts an escape, and `\G` is replaced by the
 * name of the graph.
 */
export const text = (value: string): string =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/** An id as a quoted string of the DOT language. */
export const quoted = (id: string): string =>
  `"${id.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;

export const font = (
  content: string,
  { size, color, bold }: { size?: number; color?: string; bold?: boolean } = {},
): string =>
  `<FONT${size ? ` POINT-SIZE="${size}"` : ""}${color ? ` COLOR="${color}"` : ""}>${bold ? `<B>${content}</B>` : content}</FONT>`;

export const table = (
  rows: string[],
  {
    border = 0,
    color,
    bg,
    pad = 4,
  }: { border?: number; color?: string; bg?: string; pad?: number } = {},
): string =>
  `<TABLE BORDER="${border}"${color ? ` COLOR="${color}"` : ""} CELLBORDER="0" CELLSPACING="0" CELLPADDING="${pad}"${bg ? ` BGCOLOR="${bg}"` : ""} STYLE="ROUNDED">${rows.join("")}</TABLE>`;

export const tr = (cells: string[]): string => `<TR>${cells.join("")}</TR>`;

export const td = (
  content: string,
  {
    align = "LEFT",
    colspan,
    pad,
    bg,
    port,
    rule,
  }: {
    align?: "LEFT" | "RIGHT";
    colspan?: number;
    pad?: number;
    bg?: string;
    /** a name by which an arrow can end at this cell */
    port?: string;
    /** the colour of a line above the cell */
    rule?: string;
  } = {},
): string =>
  `<TD ALIGN="${align}" BALIGN="${align}"${colspan ? ` COLSPAN="${colspan}"` : ""}${pad ? ` CELLPADDING="${pad}"` : ""}${bg ? ` BGCOLOR="${bg}"` : ""}${port ? ` PORT="${port}"` : ""}${rule ? ` BORDER="1" SIDES="T" COLOR="${rule}"` : ""}>${content}</TD>`;

/** A card: a table with an outline on the colour of a surface. */
export const card = (
  colors: GraphColors,
  rows: string[],
  { outline = colors.border, bg = colors.surface } = {},
): string => table(rows, { border: 1, color: outline, bg, pad: 5 });

/** The label of a section of a card: small capitals in quiet ink. */
export const sectionLabel = (colors: GraphColors, label: string): string =>
  font(text(label.toUpperCase()), {
    size: 7.5,
    color: colors.quiet,
    bold: true,
  });

/** A status badge: a wash of its tone with the status written in its ink. */
export const badge = (label: string, { ink, wash }: GraphTone): string =>
  table(
    [
      tr([
        td(font(text(label), { size: 8, color: ink, bold: true }), { pad: 2 }),
      ]),
    ],
    {
      bg: wash,
      pad: 2,
    },
  );
